<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { format, parseISO } from 'date-fns';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { isValidURL } from 'dashboard/helper/URLHelper';
import { getRegexp } from 'shared/helpers/Validators';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  attribute: {
    type: Object,
    default: () => ({}),
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close', 'save']);

const store = useStore();

// Form state
const editedValue = ref('');
const isLoading = ref(false);
const validationError = ref('');
const isListDropdownOpen = ref(false);

// Constants
const DATE_FORMAT = 'yyyy-MM-dd';

// Computed properties
const getAttributeIcon = (type) => {
  const iconMap = {
    'date': 'i-lucide-calendar',
    'checkbox': 'i-lucide-check-square',
    'link': 'i-lucide-link',
    'list': 'i-lucide-list',
    'number': 'i-lucide-hash',
    'text': 'i-lucide-type',
  };
  return iconMap[type] || 'i-lucide-tag';
};

const getInputType = (type) => {
  if (type === 'link') return 'url';
  if (type === 'number') return 'number';
  if (type === 'date') return 'date';
  return 'text';
};

const getFormattedEditValue = (attribute) => {
  const type = attribute.attribute_display_type;
  
  if (type === 'date' && attribute.value) {
    return format(new Date(attribute.value), DATE_FORMAT);
  }
  if (type === 'checkbox') {
    return attribute.value === true || attribute.value === 'true';
  }
  return attribute.value || '';
};


const isFormValid = computed(() => {
  if (validationError.value) return false;
  
  // For checkbox, always valid (can be true or false)
  if (props.attribute.attribute_display_type === 'checkbox') {
    return true;
  }
  
  // For other types, require a value
  if (!editedValue.value) {
    return false;
  }
  
  return true;
});

// Validation
const validateInput = () => {
  validationError.value = '';
  
  if (!editedValue.value && props.attribute.attribute_display_type !== 'checkbox') {
    return true; // Empty is allowed
  }
  
  // Regex validation if pattern exists
  if (props.attribute.regex_pattern && editedValue.value) {
    try {
      const regex = getRegexp(props.attribute.regex_pattern);
      if (!regex.test(editedValue.value)) {
        validationError.value = props.attribute.regex_cue || 'Formato inválido';
        return false;
      }
    } catch (error) {
      console.error('Invalid regex pattern:', props.attribute.regex_pattern, error);
    }
  }
  
  // URL validation for link type
  if (props.attribute.attribute_display_type === 'link' && editedValue.value && !isValidURL(editedValue.value)) {
    validationError.value = 'URL inválida';
    return false;
  }
  
  return true;
};

// Methods
const closeModal = () => {
  emit('close');
  resetForm();
};

const resetForm = () => {
  editedValue.value = '';
  validationError.value = '';
  isLoading.value = false;
  isListDropdownOpen.value = false;
};

const handleEscKey = (event) => {
  if (event.key === 'Escape' && props.show) {
    closeModal();
  }
};

const toggleListDropdown = () => {
  isListDropdownOpen.value = !isListDropdownOpen.value;
};

const selectListOption = (option) => {
  editedValue.value = option;
  isListDropdownOpen.value = false;
};

const saveAttribute = async () => {
  if (!validateInput() || !props.contact?.id) return;
  
  isLoading.value = true;
  
  try {
    let finalValue = editedValue.value;
    
    // Parse date if needed
    if (props.attribute.attribute_display_type === 'date' && editedValue.value) {
      finalValue = parseISO(editedValue.value);
    }
    
    // For checkbox, the value is already boolean
    if (props.attribute.attribute_display_type === 'checkbox') {
      finalValue = editedValue.value;
    }
    
    const customAttributes = props.contact.custom_attributes || {};
    const updatedAttributes = { ...customAttributes, [props.attribute.key]: finalValue };
    
    await store.dispatch('contacts/update', {
      id: props.contact.id,
      custom_attributes: updatedAttributes,
    });
    
    useAlert('Atributo atualizado com sucesso');
    emit('save', { key: props.attribute.key, value: finalValue });
    closeModal();
  } catch (error) {
    const errorMessage = error?.response?.message || 'Erro ao atualizar o atributo';
    useAlert(errorMessage, 'error');
  } finally {
    isLoading.value = false;
  }
};

const deleteAttribute = async () => {
  if (!props.contact?.id) return;
  
  isLoading.value = true;
  
  try {
    await store.dispatch('contacts/deleteCustomAttributes', {
      id: props.contact.id,
      customAttributes: [props.attribute.key],
    });
    
    useAlert('Atributo excluído com sucesso');
    emit('save', { key: props.attribute.key, deleted: true }); // Pass deletion info
    closeModal();
  } catch (error) {
    const errorMessage = error?.response?.message || 'Erro ao excluir o atributo';
    useAlert(errorMessage, 'error');
  } finally {
    isLoading.value = false;
  }
};

// Watchers
watch(() => props.show, (newVal) => {
  if (newVal && props.attribute) {
    editedValue.value = getFormattedEditValue(props.attribute);
  }
});

watch(() => props.attribute, (newAttr) => {
  if (newAttr && props.show) {
    editedValue.value = getFormattedEditValue(newAttr);
  }
});

// Lifecycle
onMounted(() => {
  document.addEventListener('keydown', handleEscKey);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscKey);
});
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[1000] flex items-center justify-center p-8 bg-n-slate-12/10 dark:bg-n-slate-1/10 backdrop-blur-sm"
    @click.self="closeModal"
  >
    <div class="w-full max-w-2xl max-h-[90vh] bg-n-solid-2 rounded-xl shadow-xl flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between border-b border-n-weak dark:border-n-strong p-5 flex-shrink-0">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-full bg-n-brand/10 flex items-center justify-center">
            <span :class="getAttributeIcon(attribute?.attribute_display_type)" class="text-n-brand text-lg" />
          </div>
          <div class="flex flex-col gap-0.5">
            <span class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-12">
              {{ attribute?.attribute_display_name || attribute?.key || 'Atributo' }}
            </span>
            <span v-if="attribute?.attribute_description" class="text-sm text-n-slate-11 dark:text-n-slate-11">
              {{ attribute.attribute_description }}
            </span>
          </div>
        </div>
        <button
          class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 dark:text-n-slate-12 hover:enabled:bg-n-alpha-2 dark:hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 dark:focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center"
          @click="closeModal"
        >
          <span class="i-lucide-x flex-shrink-0" />
        </button>
      </div>

      <!-- Content -->
      <div class="p-6 overflow-y-auto flex-1">
        <div class="space-y-6">
          <!-- Current Value Display -->
          <div v-if="attribute?.value" class="bg-n-slate-2 dark:bg-n-slate-7 rounded-lg p-4">
            <div class="text-xs text-n-slate-11 mb-2">Valor atual:</div>
            <div class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12">
              <span v-if="attribute?.attribute_display_type === 'checkbox'">
                {{ attribute.value === true ? 'Sim' : 'Não' }}
              </span>
              <span v-else-if="attribute?.attribute_display_type === 'date'">
                {{ new Date(attribute.value).toLocaleDateString() }}
              </span>
              <a
                v-else-if="attribute?.attribute_display_type === 'link' && isValidURL(attribute.value)"
                :href="attribute.value"
                target="_blank"
                rel="noopener noreferrer"
                class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
              >
                {{ attribute.value }}
              </a>
              <span v-else>{{ attribute.value }}</span>
            </div>
          </div>

          <!-- Form Fields -->
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-n-slate-12 dark:text-n-slate-12 mb-2">
                Novo valor:
              </label>
              
              <!-- Checkbox Input -->
              <div v-if="attribute?.attribute_display_type === 'checkbox'">
                <button
                  type="button"
                  @click="editedValue = !editedValue"
                  class="w-full px-3 py-2.5 border border-n-weak dark:border-n-weak rounded-lg text-sm bg-n-slate-2 dark:bg-n-slate-7 text-n-slate-12 dark:text-n-slate-12 focus:border-n-brand hover:bg-n-slate-3 dark:hover:bg-n-slate-6 flex justify-between items-center cursor-pointer"
                >
                  <span class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12">{{ editedValue ? 'Sim' : 'Não' }}</span>
                  <input 
                    type="checkbox"
                    v-model="editedValue"
                    class="w-4 h-4 text-n-brand bg-n-background border-n-slate-4 rounded focus:ring-n-brand dark:focus:ring-n-brand dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600 pointer-events-none"
                    @click.stop
                  />
                </button>
              </div>
              
              <!-- List Input -->
              <div v-else-if="attribute?.attribute_display_type === 'list'" class="relative">
                <button
                  type="button"
                  @click="toggleListDropdown"
                  class="w-full flex items-center justify-between reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 dark:bg-n-slate-7 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 text-n-slate-12 dark:text-n-slate-12 transition-all duration-500 ease-in-out border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 focus:border-n-brand dark:focus:border-n-brand"
                  :class="{ '!rounded-br-none !rounded-bl-none': isListDropdownOpen }"
                >
                  <span class="text-left truncate">
                    {{ editedValue || 'Clique para selecionar' }}
                  </span>
                  <span class="i-lucide-chevron-down ml-2 flex-shrink-0 transition-transform duration-200" :class="{ 'rotate-180': isListDropdownOpen }" />
                </button>
                
                <!-- Dropdown Options -->
                <transition
                  enter-active-class="transition-all duration-300 ease-out"
                  leave-active-class="transition-all duration-300 ease-in"
                  enter-from-class="opacity-0 max-h-0"
                  enter-to-class="opacity-100 max-h-60"
                  leave-from-class="opacity-100 max-h-60"
                  leave-to-class="opacity-0 max-h-0"
                >
                  <div v-if="isListDropdownOpen" class="overflow-hidden border border-t-0 border-n-weak dark:border-n-weak rounded-b-xl bg-white dark:bg-n-slate-8 p-2">
                    <div class="max-h-60 overflow-y-auto flex flex-col">
                      <button
                        v-for="option in attribute?.attribute_values || []"
                        :key="option"
                        type="button"
                        @click="selectListOption(option)"
                        class="w-full text-left text-sm hover:bg-n-slate-3 dark:hover:bg-n-slate-6 transition-colors text-n-slate-12 dark:text-n-slate-12 bg-transparent justify-start items-start px-2 py-2 rounded-xl"
                        :class="{ 'bg-n-brand/10 text-n-brand': editedValue === option }"
                      >
                        {{ option }}
                      </button>
                      <div v-if="!attribute?.attribute_values?.length" class="px-3 py-2.5 text-sm text-n-slate-10 dark:text-n-slate-10 bg-transparent">
                        Nenhuma opção disponível
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
              
              <!-- Other Input Types -->
              <div v-else>
                <label :class="{ error: validationError }">
                  <input
                    v-model="editedValue"
                    :type="getInputType(attribute?.attribute_display_type)"
                    :placeholder="attribute?.attribute_display_type === 'link' ? 'https://exemplo.com' : 'Digite o valor...'"
                    @input="validateInput"
                    @keyup.enter="saveAttribute"
                  />
                </label>
              </div>
              
              <!-- Validation Error -->
              <div v-if="validationError" class="mt-2 text-sm text-red-600 dark:text-red-400">
                {{ validationError }}
              </div>
              
              <!-- Regex Hint -->
              <div v-if="attribute?.regex_cue && !validationError" class="mt-2 text-sm text-n-slate-11 dark:text-n-slate-11">
                {{ attribute.regex_cue }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between border-t border-n-weak dark:border-n-strong p-5 flex-shrink-0">
        <div>
          <button
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-xl outline-1 outline disabled:opacity-50 bg-red-600 text-white hover:enabled:bg-red-700 focus-visible:bg-red-700 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            :disabled="isLoading"
            @click="deleteAttribute"
          >
            Remover do Contato
          </button>
        </div>
        <div class="flex gap-3">
          <button
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-xl outline-1 outline disabled:opacity-50 bg-n-slate-9/10 dark:bg-n-slate-9/20 text-n-slate-12 dark:text-n-slate-12 hover:enabled:bg-n-slate-9/20 dark:hover:enabled:bg-n-slate-9/30 focus-visible:bg-n-slate-9/20 dark:focus-visible:bg-n-slate-9/30 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            :disabled="isLoading"
            @click="closeModal"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-xl outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            :disabled="!isFormValid || isLoading"
            @click="saveAttribute"
          >
            <span v-if="isLoading" class="i-lucide-loader-2 animate-spin" />
            {{ isLoading ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Add any custom styles here if needed */
</style>